import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaCertificate, FaDownload } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Generatedcertificates.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

/** Smart page-number array with ellipses */
const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Generatedcertificates() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Data ──
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => {
    const base = { 'Content-Type': 'application/json' };
    return credentials ? { ...base, Authorization: `Basic ${credentials}` } : base;
  }, [credentials]);

  // ══════════════════════════════════════
  //  INITIAL DATA FETCHING
  // ══════════════════════════════════════

  // Credentials
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch (e) {
        console.error('Failed to get credentials', e);
        toast.error('Authentication failed');
      }
    })();
  }, []);

  // Agent from localStorage
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) setAgent(user);
    else navigate('/login');
  }, [navigate]);

  // Agent details
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch (e) {
        console.error('getDealerdetails error', e);
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // Fetch all certificates
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/getAllcertificates?code=${agentCode}`, {
          method: 'GET', headers: authHeaders()
        });
        const data = await res.json();
        setCertificates(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('getAllcertificates error', e);
        toast.error('Failed to load certificates');
        setCertificates([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  DEBOUNCED SERVER-SIDE SEARCH
  // ══════════════════════════════════════
  useEffect(() => {
    if (!searchTerm.trim() || !agentCode || !isAuth) {
      // If search cleared, refetch all certificates
      if (!searchTerm.trim() && agentCode && isAuth) {
        (async () => {
          try {
            const res = await fetch(`${API}/getAllcertificates?code=${agentCode}`, {
              method: 'GET', headers: authHeaders()
            });
            const data = await res.json();
            setCertificates(Array.isArray(data) ? data : []);
          } catch (e) { /* already loaded */ }
        })();
      }
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/getSearchcertificates?code=${agentCode}&search=${encodeURIComponent(searchTerm)}`,
          { method: 'GET', headers: authHeaders() }
        );
        const data = await res.json();
        setCertificates(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('searchCertificates error', e);
        toast.error('Search failed');
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, agentCode, isAuth, authHeaders]);

  // Reset page when search changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // ══════════════════════════════════════
  //  PAGINATED DATA
  // ══════════════════════════════════════
  const totalPages = Math.ceil(certificates.length / ROWS_PER_PAGE);
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const indexOfLast = indexOfFirst + ROWS_PER_PAGE;
  const currentRows = certificates.slice(indexOfFirst, indexOfLast);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="certs-page">
      <Sidebar />
      <div className="certs-main">
        <Header userName={agentName || agent || 'Agent'} />
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Top bar */}
        <div className="certs-top-row">
          <span className="certs-company">Definite Assurance</span>
          <span className="certs-breadcrumb">Dashboard / Generated Certificates</span>
        </div>

        <div className="certs-card">
          <h3><FaCertificate style={{ marginRight: 8 }} /> Generated Certificates</h3>

          {/* Toolbar */}
          <div className="certs-toolbar">
            <div className="certs-search-wrap">
              <FaSearch className="certs-search-icon" />
              <input
                type="text"
                placeholder="Search by registration…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <span className="certs-stat">
              Total: <strong>{certificates.length}</strong>
            </span>
          </div>

          {/* Loading */}
          {loading && (
            <div className="certs-loading">
              <div className="certs-spinner" />
              <p>Loading certificates…</p>
            </div>
          )}

          {/* Empty state */}
          {!loading && certificates.length === 0 && (
            <div className="certs-empty">
              <FaCertificate size={32} color="#ccc" style={{ marginBottom: 10 }} />
              <p>{searchTerm ? 'No certificates match your search.' : 'No generated certificates found.'}</p>
            </div>
          )}

          {/* Table */}
          {!loading && certificates.length > 0 && (
            <>
              <div className="certs-table-wrap">
                <table className="certs-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Registration</th>
                      <th>Valid From</th>
                      <th>Expiry Date</th>
                      <th>Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, index) => (
                      <tr key={val.OID || index}>
                        <td>{indexOfFirst + index + 1}</td>
                        <td>{val.Narration || '--'}</td>
                        <td>{formatISODate(val.ValidFrom)}</td>
                        <td>{formatISODate(val.ExpiryDate)}</td>
                        <td>
                          {val.CertificatePdf ? (
                            <a
                              href={val.CertificatePdf}
                              target="_blank"
                              rel="noreferrer"
                              className="certs-download-link"
                            >
                              <FaDownload /> Download
                            </a>
                          ) : (
                            <span style={{ color: '#aaa', fontSize: '0.8rem' }}>N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="certs-pagination">
                <span className="certs-pagination-info">
                  Showing {indexOfFirst + 1} to {Math.min(indexOfLast, certificates.length)} of {certificates.length} entries
                </span>
                <div className="certs-pagination-controls">
                  <button
                    className="certs-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {pageNumbers.map((page, i) =>
                    page === '...'
                      ? <span key={`e${i}`} className="certs-page-ellipsis">…</span>
                      : (
                        <button
                          key={page}
                          className={`certs-page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      )
                  )}
                  <button
                    className="certs-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
